import ColoredRectangle from "$root/components/icons/ColoredRectangle";
import { Divider } from "$root/components/Divider";
import { useLocaleFormatters } from "$root/localization/hooks";
import { Highcharts } from "$root/third-party-integrations/highcharts-with-modules";
import { getThemeCssVars } from "$root/utils/theme";
import { Text } from "@mdotm/shadcn/legacy/components";
import { useRefProxy, type NodeOrFn } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import HighchartsReact, { type HighchartsReactRefObject } from "highcharts-react-official";
import { type ForwardedRef, type ReactNode, useMemo } from "react";
import { renderToString } from "react-dom/server";
import { keyMetricThresholdColor } from "./categorical-palette";
import { DensityChartLegend } from "./DensityChartLegend";
import {
	ChartCardShell,
	chartHeightInPx,
	chartTitleNode,
	ChartWallSwitch,
	type ChartHeight,
	type ChartWallStatus,
} from "./ChartCardShell";

/**
 * Density: the charted metric on the X axis, probability density on the Y axis, each series' mean
 * drawn as a vertical line.
 *
 * **Why the X axis carries metric values and not z-scores.** Each series is standardized by its own
 * mean and σ, and per-series z-scores cannot share an axis: z = 1 is a different actual value for
 * CURRENT than for PROPOSAL, so drawing both against one z axis would show the two curves centred on
 * each other and hide the very shift an overlay exists to reveal. Real values keep one honest axis
 * shared by both series; the z reading is per series, and lives in the mean lines and the tooltip.
 *
 * **Why the Y axis is a density and not a count.** `count / n / binWidth` integrates to 1, so two
 * series of different n are comparable — and with an overlay they almost never share an n, because a
 * portfolio without a proposal contributes no PROPOSAL value. Two count curves of different heights
 * would invite reading a sample-size difference as a distribution difference.
 *
 * **Area, plus a fitted-normal reference.** `areaspline` over the backend's bins for the empirical
 * shape, and a dashed `spline` through φ((x − μ) / σ) / σ for what a normal of the same μ and σ would
 * look like — the gap between the two *is* the skew and the fat tails. Both overlays the areas carry —
 * the fitted curves and the mean lines — are drawn under a flag of their own (`showNormalFit`,
 * `showMeanLines`), and solid versus dashed is what separates the two. Deliberately *not* Highcharts'
 * `bellcurve` series (from the already-registered `histogram-bellcurve` module): it derives its curve
 * from a `baseSeries` of raw client-side values, and this chart never sees them — the backend
 * aggregates over the whole predicate result, not over a page the frontend fetched. Same reason
 * `FrequencyChart` does not use the `histogram` series.
 *
 * No band-drag selection, unlike the frequency chart: a density curve is a statement about the
 * group, and `itemUuids` are per *bin* — a band over a smoothed area does not name the portfolios
 * under it any more precisely than the histogram already does.
 *
 * **"Highlight proposal" hides CURRENT here, where every other kind dims it.** The deliberate
 * exception: the other kinds separate their two sources spatially — side-by-side bars, a solid line
 * against a dashed one — so dimming one is enough to push it back. Two filled density areas overlap
 * across most of the plot instead, and a dimmed area under a translucent one just muddies both; the
 * toggle reads as doing nothing. Hiding is what "highlight" can actually mean for this shape. The
 * toggle that hid the series is also what brings it back: the legend below the plot is a static key —
 * see {@link DensityChartLegend} — so there is nothing to click there, and it can be switched off
 * entirely.
 */

export type DensityChartSeriesStats = {
	mean: number;
	/** 0 — never null — when the series has fewer than two values. The curve and the z are then dropped. */
	standardDeviation: number;
	count: number;
};

export type DensityChartBin = {
	from: number;
	to: number;
	/** Bin midpoint: the density describes the whole bin, so it is plotted at its centre. */
	center: number;
	label: string;
	/** `[CURRENT, PROPOSAL]` probability density. */
	density: [number, number | null];
	/** `[CURRENT, PROPOSAL]` raw counts, for the tooltip — a bare density says nothing about how many. */
	counts: [number, number | null];
};

export type DensityChartProps = {
	innerRef?: ForwardedRef<HighchartsReactRefObject>;
	chartTitle: ReactNode;
	mainMetricLabel: string;
	secondaryMetricLabel: string;
	bins: Array<DensityChartBin>;
	/** `[CURRENT, PROPOSAL]`. Drives the mean lines, the reference curves and the z in the tooltip. */
	stats: [DensityChartSeriesStats, DensityChartSeriesStats | null];
	/** Fitted-normal reference points per source, sampled on their own grid by the mapper. */
	normalCurves: [Array<[number, number]>, Array<[number, number]> | null];
	/** @default false */
	hasProposal?: boolean;
	/**
	 * Hides the {@link mainMetricLabel} series — area, fitted curve, mean line and tooltip row — so the
	 * proposal is read on its own. Hides rather than dims, unlike the other chart kinds; see the note on
	 * the component. Ignored without a proposal to highlight.
	 */
	highlightSecondaryMetric?: boolean;
	/**
	 * Draws a vertical mean line per drawn series, labelled with that series' source.
	 *
	 * Independent of {@link showNormalFit} in both directions, which is the point of there being two flags:
	 * the fit is *built* on the mean, so it is tempting to treat the mean as its prerequisite, but "where
	 * does this group sit" and "how far from normal is its shape" are two questions and a reader can want
	 * either answer without the other.
	 *
	 * @default true
	 */
	showMeanLines?: boolean;
	/**
	 * Draws the fitted-normal reference curve over each area — see {@link normalCurves}. Independent of
	 * {@link showMeanLines}.
	 *
	 * @default true
	 */
	showNormalFit?: boolean;
	/**
	 * Draws the key under the plot — {@link DensityChartLegend}. Persisted as `chartLegend.visible` and set
	 * by the panel's Show-legend checkbox, like the two chart kinds whose legend is Highcharts' own; see
	 * `chartTypeDrawsLegend`. Hiding it gives the plot the whole of {@link chartHeight}.
	 *
	 * @default true
	 */
	showLegend?: boolean;
	/** e.g. % — the unit of the charted metric, i.e. of the X axis. */
	unit?: string;
	/**
	 * The key-metric threshold (FR-8), as a value on the charted metric's scale — so the line is
	 * **vertical** here, unlike the column chart's horizontal one. Read-only: it is not draggable, see the
	 * note where it is built.
	 */
	threshold?: number;
	midActions?: NodeOrFn;
	actions?: NodeOrFn;
	/**
	 * Rendered under the header, above the plot: the level row of a chart whose title cannot carry its
	 * levels.
	 */
	subTitle?: NodeOrFn;
	/** @default 'tall' */
	chartHeight?: ChartHeight;
};

/**
 * `(value − μ) / σ` — how many standard deviations a value sits from its series' own mean.
 *
 * Lives with the chart rather than in the workspace mappers because the tooltip is its only reader: the
 * chart is handed `stats` as a prop precisely so it needs to know nothing about where they came from, and
 * a ui-lib component reaching into `functional-areas` for arithmetic would invert that.
 *
 * `null` when σ is 0 — one portfolio, or a group whose values are all identical — because the z-score is
 * then genuinely undefined. Saying so beats printing the `Infinity` the division yields, or the confident
 * `0` that a value sitting on a mean it defines by itself would suggest.
 */
export function zScoreOf(value: number, stats: DensityChartSeriesStats | null): number | null {
	if (!stats || stats.standardDeviation <= 0) {
		return null;
	}
	return (value - stats.mean) / stats.standardDeviation;
}

/** Enough decimals that a density of 0.0043 does not render as "0". */
const densityDecimalPlaces = 4;

/**
 * How opaque a filled density area is.
 *
 * Not 1: the fitted-normal reference curve is drawn in the same colour as its own area, so over a
 * fully opaque fill it disappears — and the whole point of the reference is to be compared against
 * the empirical shape. With two series it is worse, since the later area would hide the earlier one.
 *
 * This only works because the series colour is *resolved* first — see {@link resolvedChartColor}.
 */
const areaFillOpacity = 0.8;

/**
 * The palette entry as a real colour (`#00aeef`) rather than as `var(--palette-S400)`.
 *
 * Highcharts derives an area's fill by parsing the series colour and re-emitting it with the
 * requested alpha. Its colour parser cannot read a CSS custom property, so handing it the raw
 * `themeCSSVars` string makes `fillOpacity` **silently inert** — the fill renders fully opaque, and
 * nothing warns you. Resolving first is what makes the alpha take effect.
 *
 * Called at render rather than at module load on purpose: `getThemeCssVars` reads computed styles
 * off `document.body`, so it needs a browser and a theme that has actually been applied. Falls back
 * to the variable itself where that is not true (jsdom, a test renderer) — plain `stroke`/`fill`
 * accept `var()` fine, so the chart still draws; only the alpha is lost.
 */
function resolvedChartColor(cssVar: string): string {
	const resolved = getThemeCssVars(cssVar);
	return resolved?.trim() ? resolved.trim() : cssVar;
}

/** Where the first mean label sits below the top of the plot, and how far each next one drops. */
const meanLabelTopOffset = 12;
const meanLabelRowHeight = 14;

/**
 * The plotted density polygon for one source: the per-bin densities at their midpoints, plus a zero
 * anchor at each end.
 *
 * The anchors are the whole point. Densities belong at bin *midpoints*, so a bare polygon spans only
 * `[first.center, last.center]` — half a bin short at each end. The filled area the eye reads as
 * "the probability" is then visibly less than 1, and the curve starts with a vertical cliff at the
 * first midpoint that looks like clipped data rather than a distribution. Anchoring to zero one half
 * bin beyond each end is the standard frequency-polygon convention: the curve descends to zero on
 * both sides and the drawn area equals the true total.
 *
 * Every bin is the same width by `DistributionBinner`'s contract — the invariant the shared binning
 * rule exists to hold — so the first bin's width speaks for all of them.
 */
export function zeroAnchoredDensity(bins: Array<DensityChartBin>, source: 0 | 1): Array<[number, number]> {
	if (bins.length === 0) {
		return [];
	}
	const width = bins[0].to - bins[0].from;
	const points = bins.map((bin): [number, number] => [bin.center, bin.density[source] ?? 0]);
	// A degenerate width would put both anchors on top of the data; leave the polygon unanchored
	// rather than draw a spike out of nothing.
	if (!(width > 0)) {
		return points;
	}
	return [[bins[0].from - width / 2, 0], ...points, [bins[bins.length - 1].to + width / 2, 0]];
}

export function DensityChart(props: DensityChartProps): React.JSX.Element {
	const highchartsRef = useRefProxy(null, props.innerRef);
	const unit = props.unit ?? "";
	const { formatNumber } = useLocaleFormatters();
	const hasProposal = props.hasProposal ?? false;
	// Nothing to highlight *against* without a second series, so the toggle waits for one. Read by the
	// options and by the legend below the plot, which drops the entry for a series the plot has hidden.
	const hideMain = (props.highlightSecondaryMetric ?? false) && hasProposal;

	/**
	 * Memoized for the same reason the options are: `containerProps` is the *other* entry in the dependency
	 * list of the layout effect that calls `chart.update`, so an object literal here re-inits every series on
	 * every render however stable the options object is.
	 */
	const containerProps = useMemo(() => ({ style: { height: "100%", width: "100%" } }), []);

	const chartOptions = useMemo((): Highcharts.Options => {
		const showMeanLines = props.showMeanLines ?? true;
		const showNormalFit = props.showNormalFit ?? true;

		// Resolved once per render and reused for the series, the mean line and the tooltip swatch, so
		// the three cannot drift apart.
		const mainColor = resolvedChartColor(themeCSSVars.palette_S400);
		const secondaryColor = resolvedChartColor(themeCSSVars.palette_B400);

		const sources = [
			{
				label: props.mainMetricLabel,
				color: mainColor,
				hidden: hideMain,
				points: zeroAnchoredDensity(props.bins, 0),
				curve: props.normalCurves[0],
				stats: props.stats[0],
				source: 0 as const,
			},
			...(hasProposal
				? [
						{
							label: props.secondaryMetricLabel,
							color: secondaryColor,
							hidden: false,
							points: zeroAnchoredDensity(props.bins, 1),
							curve: props.normalCurves[1] ?? [],
							stats: props.stats[1],
							source: 1 as const,
						},
					]
				: []),
		];
		const visibleSources = sources.filter((source) => !source.hidden);

		const series = sources.flatMap((source, index): Array<Highcharts.SeriesOptionsType> => {
			const id = `density-${index}`;
			const drawn: Array<Highcharts.SeriesOptionsType> = [
				{
					type: "areaspline",
					id,
					name: source.label,
					color: source.color,
					// Hidden rather than dimmed under "Highlight proposal" — see the note on the component. The
					// linked normal-fit curve follows its parent's visibility automatically, which is the reason
					// the link survives the legend's removal.
					visible: !source.hidden,
					// Translucent so the fitted-normal curve drawn over it — and, with two series, the area
					// underneath — stay readable. Only the fill: the outline keeps full strength, which is
					// what separates this from dimming the whole series group.
					fillOpacity: areaFillOpacity,
					lineWidth: 2,
					data: source.points,
					custom: { source: source.label, kind: "empirical" },
				},
			];

			if (showNormalFit && source.curve.length > 0) {
				drawn.push({
					type: "spline",
					// An `id` of its own, like the area's, because this series is *conditional*: a source with
					// fewer than two values contributes no curve, which shortens the list and shifts every later
					// slot. `chart.update(…, oneToOne: true)` matches by position unless a series declares an id,
					// and would then hand the *second* source's area the options of a curve — turning that area
					// into a second copy of its own fitted line.
					id: `${id}-normal-fit`,
					name: `${source.label} (normal fit)`,
					color: source.color,
					dashStyle: "Dash",
					lineWidth: 1,
					// Linked to the empirical area so it appears and disappears with it: the reference is that
					// same series idealized, and a fitted curve left behind by a hidden area is a line out of
					// nowhere.
					linkedTo: id,
					enableMouseTracking: false,
					marker: { enabled: false },
					data: source.curve,
					custom: { source: source.label, kind: "normal" },
				});
			}

			return drawn;
		});

		// The mean, as a vertical line per series — the chart's headline fact. `zIndex` above the areas,
		// or a filled area drawn after it would bury it.
		//
		// Solid, where the fitted curves are dashed. The two are the same series' two statements — where it
		// sits, and what a normal of its μ and σ would look like — so the stroke style is what tells them
		// apart, and each is drawn under a flag of its own.
		//
		// Labels are stacked one row per series rather than all pinned to the top of the plot. Two means
		// that are close together (which is the interesting case — a proposal that shifted the group only
		// slightly) would otherwise render their labels at the same y and overlap into an unreadable
		// smudge, exactly when the reader most needs to tell them apart.
		// Only the visible sources: a mean line for a hidden series is an unexplained vertical rule, and
		// the stagger below has to close the gap left by whatever is hidden rather than leave a hole.
		const meanLines = (showMeanLines ? visibleSources : []).flatMap(
			(source, index): Array<Highcharts.XAxisPlotLinesOptions> =>
				source.stats && source.stats.count > 0
					? [
							{
								value: source.stats.mean,
								color: source.color,
								width: 2,
								dashStyle: "Solid",
								zIndex: 5,
								label: {
									text: `${source.label} mean ${formatNumber(source.stats.mean, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`,
									rotation: 0,
									// `align: "right"` ends the text *at* the line, so `x: -4` clears the 2px stroke and
									// leaves the label reading leftwards out of it rather than printed over it.
									align: "right",
									x: -4,
									y: meanLabelTopOffset + index * meanLabelRowHeight,
									style: { color: source.color, fontSize: "10px" },
								},
							},
						]
					: [],
		);

		// The key-metric threshold, on the same axis and in the same units as the means — the X axis carries
		// metric values here, so the plot line's `value` *is* the threshold, with no mapping in between.
		//
		// Styled against the mean lines on purpose: neutral where they are series-coloured, and labelled to
		// the right of the line where they label to the left. It marks something the user chose rather than
		// something the data said, and with two means already on the axis a third coloured rule would read
		// as a third series. Those two differences carry it alone — solid is not one of them, that being
		// what tells a mean line from its own fitted curve.
		//
		// Not draggable, unlike the column chart's: that one converts a pointer delta into a value through
		// its y-axis' own pixel geometry, which does not transfer to an X axis (and transfers to the
		// frequency chart's categorical X axis not at all). The threshold is edited in the settings
		// panel here.
		const thresholdLine: Array<Highcharts.XAxisPlotLinesOptions> =
			props.threshold == null
				? []
				: [
						{
							value: props.threshold,
							color: keyMetricThresholdColor,
							width: 2,
							dashStyle: "Solid",
							// Above the areas *and* above the mean lines, so it stays readable where they cross.
							zIndex: 6,
							label: {
								text: `Threshold ${formatNumber(props.threshold, { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`,
								rotation: 0,
								align: "left",
								x: 4,
								// One row below the last mean label, continuing their stagger. Opposite alignment is
								// not enough on its own: a threshold that sits just left of a mean puts this label
								// (drawn rightwards) straight through that one (drawn leftwards), and the closer the
								// two lines the worse it reads — the same overlap the means stagger to avoid. Keyed
								// off the mean lines actually drawn, not the visible sources, so a source without
								// stats leaves no gap.
								y: meanLabelTopOffset + meanLines.length * meanLabelRowHeight,
								style: { color: themeCSSVars.palette_N500, fontSize: "10px" },
							},
						},
					];

		return {
			chart: { type: "areaspline", style: { fontFamily: "Gotham,sans-serif" } },
			title: { text: "" },
			credits: { enabled: false },
			// Highcharts' own legend stays off; the key below the plot is `DensityChartLegend`, drawn in
			// React. Highcharts would list every series it draws, and this chart draws two per source — a
			// source's fitted curve would get a legend entry of its own rather than the one shared "normal fit"
			// entry the dash actually needs.
			legend: { enabled: false },
			xAxis: {
				title: { text: "" },
				tickWidth: 0,
				plotLines: [...meanLines, ...thresholdLine],
				labels: {
					formatter(ctx) {
						return `${formatNumber(Number(ctx.value), { minDecimalPlaces: 0, maxDecimalPlaces: 2 })}${unit}`;
					},
				},
			},
			yAxis: {
				gridLineDashStyle: "Dash",
				min: 0,
				title: { text: "Probability density" },
				labels: {
					formatter(ctx) {
						return formatNumber(Number(ctx.value), { minDecimalPlaces: 0, maxDecimalPlaces: densityDecimalPlaces });
					},
				},
			},
			plotOptions: {
				areaspline: {
					animation: { duration: 0 },
					shadow: false,
					marker: { enabled: false, states: { hover: { enabled: true } } },
				},
				spline: { animation: { duration: 0 }, shadow: false },
			},
			tooltip: {
				useHTML: true,
				padding: 0,
				borderWidth: 0,
				shared: true,
				shadow: true,
				formatter(this) {
					// Looked up by x, NOT by point index: the series carries a zero anchor at each end
					// (see `zeroAnchoredDensity`), so index 0 is an anchor rather than the first bin.
					// Both x values come from the same `bin.center`, so the equality is exact.
					const x = Number(this.x);
					const bin = props.bins.find((candidate) => candidate.center === x);
					// An anchor, or the flat tail beyond the data: nothing to describe.
					if (!bin) {
						return "";
					}

					// Mirrors the plot: a hidden series contributes no row, or the tooltip would report a
					// density for a curve that is not on screen.
					const rows = visibleSources.map(({ label, color, source }) => ({ label, color, source }));

					return renderToString(
						<div className="p-2 rounded-sm bg-white min-w-60 shadow-sm flex flex-col gap-2">
							<div className="py-1 rounded-sm bg-[#EFF0F3] text-center">
								{/* The label already carries the unit on both edges (the mapper's `formatValue`
								    appends it), so appending it again here renders "[18.94%, 25.25%) %". */}
								<Text type="Body/M/Bold">{bin.label}</Text>
							</div>
							{rows.map((row, rowIndex) => {
								// The bin's midpoint is what the density describes and what the point is plotted at, so it
								// is the value the z is taken of. `null` where the series has no dispersion — see `zScoreOf`.
								const z = zScoreOf(bin.center, props.stats[row.source]);
								return (
									// biome-ignore lint/suspicious/noArrayIndexKey: fixed two-row list, rendered to a string
									<div key={rowIndex} className="flex flex-col gap-1">
										{rowIndex > 0 && <Divider direction="horizontal" />}
										<div className="flex justify-between">
											<div className="flex items-center gap-1">
												<ColoredRectangle variant="vertical" color={row.color} />
												<Text type="Body/M/Book">{row.label}</Text>
											</div>
											<Text type="Body/M/Bold">
												{formatNumber(bin.density[row.source] ?? 0, {
													minDecimalPlaces: 0,
													maxDecimalPlaces: densityDecimalPlaces,
												})}
											</Text>
										</div>
										<div className="flex justify-between">
											<Text type="Body/S/Book">Z-score</Text>
											<Text type="Body/S/Bold">
												{z == null
													? "—"
													: `${z > 0 ? "+" : ""}${formatNumber(z, { minDecimalPlaces: 1, maxDecimalPlaces: 2 })}σ`}
											</Text>
										</div>
										<div className="flex justify-between">
											<Text type="Body/S/Book">Portfolios</Text>
											<Text type="Body/S/Bold">
												{formatNumber(bin.counts[row.source] ?? 0, { maxDecimalPlaces: 0 })}
											</Text>
										</div>
									</div>
								);
							})}
						</div>,
					);
				},
			},
			series,
			navigator: { enabled: false },
			exporting: { enabled: false },
		};
		// The props this reads, one by one, rather than `props` itself — which is a fresh object every render, so
		// depending on it gave these options a new identity every time, and Highcharts' React wrapper keys its
		// `chart.update(options, redraw, oneToOne)` on exactly that identity.
	}, [
		props.bins,
		props.mainMetricLabel,
		props.normalCurves,
		props.secondaryMetricLabel,
		props.showMeanLines,
		props.showNormalFit,
		props.stats,
		props.threshold,
		unit,
		formatNumber,
		hasProposal,
		hideMain,
	]);

	return (
		<ChartCardShell
			fullHeight={props.chartHeight === "full"}
			title={chartTitleNode(props.chartTitle)}
			subTitle={props.subTitle}
			midActions={props.midActions}
			actions={props.actions}
		>
			{/* Centred under the plot, where the chart types that draw a Highcharts legend put theirs. The
			    plot takes what the legend row leaves of `chartHeight`, so the key costs plot height rather
			    than card height — the same trade a `verticalAlign: "bottom"` legend makes. */}
			<div className="flex flex-col" style={{ height: chartHeightInPx(props.chartHeight ?? "tall") }}>
				<div className="grow min-h-0">
					<HighchartsReact
						containerProps={containerProps}
						highcharts={Highcharts}
						options={chartOptions}
						ref={highchartsRef}
					/>
				</div>
				{(props.showLegend ?? true) && (
					<DensityChartLegend
						mainMetricLabel={props.mainMetricLabel}
						secondaryMetricLabel={props.secondaryMetricLabel}
						hasProposal={hasProposal}
						hideMainMetric={hideMain}
						showNormalFit={props.showNormalFit}
					/>
				)}
			</div>
		</ChartCardShell>
	);
}

export function DensityChartWall({
	status,
	...rest
}: DensityChartProps & { status: ChartWallStatus }): React.JSX.Element {
	return (
		<ChartWallSwitch
			status={status}
			chartTitle={rest.chartTitle}
			subTitle={rest.subTitle}
			midActions={rest.midActions}
			actions={rest.actions}
			chartHeight={rest.chartHeight}
		>
			{() => <DensityChart {...rest} />}
		</ChartWallSwitch>
	);
}
